import React, { useState, useEffect, useRef } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import toast from 'react-hot-toast';
import { adminScannerService } from '../../services/adminScannerService';
import { Loader2, QrCode, Search, CheckCircle, AlertTriangle, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const Scanner = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const [scanState, setScanState] = useState('idle'); // idle, scanning, found, success, permission_error
    const [member, setMember] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const scannerRef = useRef(null);

    // Cleanup
    useEffect(() => {
        return () => {
            if (scannerRef.current && scannerRef.current.isScanning) {
                scannerRef.current.stop().catch(err => console.error("Failed to stop scanner", err));
            }
        };
    }, []);

    const startScanning = async () => {
        setScanState('scanning');

        setTimeout(async () => {
            try {
                const scanner = new Html5Qrcode("reader", {
                    formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
                    verbose: false
                });
                scannerRef.current = scanner;

                await scanner.start(
                    { facingMode: "environment" },
                    {
                        fps: 10,
                        qrbox: { width: 250, height: 250 },
                        aspectRatio: 1.0,
                    },
                    (decodedText) => {
                        handleScanSuccess(decodedText);
                    },
                    (errorMessage) => {
                        // ignore
                    }
                );
            } catch (err) {
                console.error("Error starting scanner:", err);
                setScanState('permission_error');
                toast.error("Could not access camera. Please check permissions.");
            }
        }, 100);
    };

    const stopScanning = async () => {
        if (scannerRef.current) {
            try {
                await scannerRef.current.stop();
                scannerRef.current = null;
            } catch (err) {
                console.error("Failed to stop scanner", err);
            }
        }
        setScanState('idle');
    };

    const handleScanSuccess = async (decodedText) => {
        await stopScanning();
        try {
            const response = await adminScannerService.search(decodedText);
            setMember(response.data.user);
            setScanState('found');
        } catch (error) {
            console.error("User not found", error);
            toast.error("Member not found! Please check the QR code.");
            setScanState('idle');
        }
    };

    const handleSearch = async (e) => {
        e.preventDefault();
        try {
            const response = await adminScannerService.search(searchQuery);
            setMember(response.data.user);
            setScanState('found');
        } catch (error) {
            console.error("User not found", error);
            toast.error("Member not found! Please check the phone/ID.");
        }
    };

    const handleAddStamp = async () => {
        if (member && !isProcessing) {
            setIsProcessing(true);
            try {
                const response = await adminScannerService.addStamp(member.id);
                if (response.data.added) {
                    const newStamps = response.data.currentStamps;
                    const maxStamps = response.data.maxStamps || 6;

                    setMember(prev => ({ ...prev, currentStamps: newStamps }));

                    if (newStamps >= maxStamps) {
                        toast.success("Stamp Added! Reward Available!", { duration: 4000 });
                    } else {
                        setScanState('success');
                        setTimeout(() => setScanState('idle'), 2000);
                    }
                } else {
                    toast.warning("Member has reached max stamps!");
                }
            } catch (error) {
                console.error("Failed to add stamp", error);
                toast.error("Failed to add stamp.");
            } finally {
                setIsProcessing(false);
            }
        }
    };

    const handleRedeem = async () => {
        if (member && !isProcessing) {
            setIsProcessing(true);
            try {
                const response = await adminScannerService.redeem(member.id);
                toast.success("Reward Redeemed! Stamps reset.", { duration: 4000 });
                setMember(prev => ({ ...prev, currentStamps: response.data.currentStamps }));
                setScanState('idle');
            } catch (error) {
                console.error("Failed to redeem", error);
                toast.error("Failed to redeem reward.");
            } finally {
                setIsProcessing(false);
            }
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 p-6 md:p-8 font-sans text-[#31415A]">
            <div className="max-w-xl mx-auto">
                <div className="text-center mb-8">
                    <h1 className="text-2xl font-semibold text-[#31415A]">Membership Scanner</h1>
                    <p className="text-gray-500 text-sm mt-1">Scan customer QR or input phone number to add stamps.</p>
                </div>

                <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 relative min-h-[400px]">

                    {/* State: Idle */}
                    {scanState === 'idle' && (
                        <div className="p-5 md:p-8">
                            <div
                                onClick={startScanning}
                                className="bg-gray-100 rounded-xl h-48 flex flex-col items-center justify-center text-gray-400 mb-6 border-2 border-dashed border-gray-300 cursor-pointer hover:bg-gray-50 transition-colors group"
                            >
                                <div className="p-4 rounded-full bg-white shadow-sm mb-3 group-hover:scale-110 transition-transform">
                                    <QrCode className="w-8 h-8 text-[#31415A]" />
                                </div>
                                <span className="text-sm font-medium">Tap to Scan QR Code</span>
                            </div>

                            <div className="relative flex py-2 items-center">
                                <div className="flex-grow border-t border-gray-200"></div>
                                <span className="flex-shrink-0 mx-4 text-gray-400 text-xs uppercase tracking-widest">Or Manual Input</span>
                                <div className="flex-grow border-t border-gray-200"></div>
                            </div>

                            <form onSubmit={handleSearch} className="mt-6">
                                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Member Phone / ID</label>
                                <div className="flex flex-col md:flex-row gap-3 md:gap-2 items-stretch md:items-center">
                                    <input
                                        type="text"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        placeholder="e.g. 0812..."
                                        className="w-full md:flex-1 h-12 bg-gray-50 border border-gray-200 rounded-lg px-4 outline-none focus:ring-2 focus:ring-[#31415A] text-gray-900 transition-all font-medium"
                                    />
                                    <button type="submit" className="w-full md:w-auto h-12 bg-[#31415A] text-[#F7F1CD] px-6 rounded-lg font-medium hover:bg-[#253246] transition-colors shadow-lg hover:shadow-xl flex items-center justify-center">
                                        Find
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {/* State: Scanning */}
                    {scanState === 'scanning' && (
                        <div className="p-4 bg-black relative min-h-[400px] flex flex-col">
                            <div id="reader" className="w-full h-full flex-1 rounded-lg overflow-hidden bg-black"></div>
                            <button
                                onClick={stopScanning}
                                className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-white text-black px-6 py-2 rounded-full font-bold shadow-lg z-10"
                            >
                                Stop Scanning
                            </button>
                        </div>
                    )}

                    {/* State: Found */}
                    {scanState === 'found' && member && (
                        <div className="p-8">
                            <div className="flex items-center justify-between mb-8">
                                <button onClick={() => setScanState('idle')} className="text-gray-400 hover:text-gray-600 flex items-center gap-1 text-sm">
                                    <ArrowLeft className="w-4 h-4" /> Back
                                </button>
                                <span className="text-green-600 bg-green-50 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">Member Verified</span>
                            </div>

                            <div className="text-center mb-8">
                                <div className="w-20 h-20 bg-[#31415A] text-[#F7F1CD] rounded-full mx-auto flex items-center justify-center text-2xl font-bold mb-4 uppercase">
                                    {member.name.charAt(0)}
                                </div>
                                <h2 className="text-2xl font-bold text-[#31415A]">{member.name}</h2>
                                <p className="text-gray-500 text-sm">{member.phone}</p>
                            </div>

                            <div className="grid grid-cols-2 gap-4 mb-8">
                                <div className="bg-gray-50 p-4 rounded-xl text-center border border-gray-100">
                                    <span className="block text-gray-400 text-xs font-bold uppercase tracking-wider mb-1">Current Stamps</span>
                                    <span className="text-3xl font-bold text-[#31415A]">{member.currentStamps} <span className="text-gray-400 text-lg">/ {member.maxStamps}</span></span>
                                </div>
                                <div className="bg-gray-50 p-4 rounded-xl text-center border border-gray-100">
                                    <span className="block text-gray-400 text-xs font-bold uppercase tracking-wider mb-1">Last Visit</span>
                                    <span className="text-lg font-medium text-gray-700 mt-1 text-sm">{member.lastVisit}</span>
                                </div>
                            </div>

                            {member.currentStamps >= member.maxStamps ? (
                                <div className="space-y-3">
                                    <div className="bg-amber-100 border border-amber-200 text-amber-800 p-4 rounded-xl text-sm font-medium text-center">
                                        🎉 Customer eligible for Reward!
                                    </div>
                                    <button
                                        onClick={handleRedeem}
                                        disabled={isProcessing}
                                        className="w-full bg-amber-500 text-white py-4 rounded-xl font-bold text-lg hover:shadow-lg hover:scale-[1.02] transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                                    >
                                        {isProcessing ? (
                                            <>
                                                <Loader2 className="w-6 h-6 animate-spin" />
                                                Processing...
                                            </>
                                        ) : (
                                            'Redeem Reward'
                                        )}
                                    </button>
                                    <button
                                        onClick={() => setScanState('idle')}
                                        className="w-full bg-gray-100 text-gray-600 py-3 rounded-xl font-medium hover:bg-gray-200 transition-all"
                                    >
                                        Save for Later
                                    </button>
                                </div>
                            ) : (
                                <button
                                    onClick={handleAddStamp}
                                    disabled={isProcessing}
                                    className="w-full bg-[#31415A] text-[#F7F1CD] py-4 rounded-xl font-bold text-lg hover:shadow-lg hover:scale-[1.02] transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                                >
                                    {isProcessing ? (
                                        <>
                                            <Loader2 className="w-6 h-6 animate-spin" />
                                            Processing...
                                        </>
                                    ) : (
                                        <>
                                            <CheckCircle className="w-6 h-6" />
                                            Add Stamp
                                        </>
                                    )}
                                </button>
                            )}
                        </div>
                    )}

                    {/* State: Success */}
                    {scanState === 'success' && (
                        <div className="absolute inset-0 bg-[#31415A] flex flex-col items-center justify-center text-[#F7F1CD] animate-in fade-in zoom-in duration-300">
                            <div className="w-24 h-24 bg-[#F7F1CD] text-[#31415A] rounded-full flex items-center justify-center mb-6 animate-bounce">
                                <CheckCircle className="w-12 h-12" />
                            </div>
                            <h2 className="text-3xl font-bold mb-2">Stamp Added!</h2>
                            <p className="opacity-80">New balance: {member?.currentStamps}</p>
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
};

export default Scanner;
